import React, { useEffect, useMemo, useState } from "react";
import "./portfolioform.css";

import {
  FaPlus,
  FaImage,
  FaTrash,
  FaUpload,
  FaSpinner,
  FaLink,
  FaAlignLeft,
  FaMobileAlt,
  FaGlobe,
  FaPalette,
  FaCheckCircle,
  FaTimesCircle,
  FaEdit,
  FaSave,
  FaSortNumericDown,
  FaExternalLinkAlt,
  FaTimes,
  FaSyncAlt,
  FaSearch,
  FaFilter,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaImages,
  FaUndo,
} from "react-icons/fa";
import { BASE_URL } from "../config";
// =========================================================
// API CONFIG
// =========================================================

const API_URL = `${BASE_URL}portfolio.php`;

const IMAGE_URL = `${BASE_URL}portfolio_uploads/`;

// =========================================================
// COMPONENT
// =========================================================

export default function Portfolioform() {
  // =========================================================
  // FORM
  // =========================================================

  const [formData, setFormData] = useState({
    title: "",
    category: "Mobile",
    app_link: "",
    display_order: 1,
  });

  // =========================================================
  // PROJECTS
  // =========================================================

  const [projects, setProjects] = useState([]);

  // =========================================================
  // EDIT
  // =========================================================

  const [editingId, setEditingId] = useState(null);

  // =========================================================
  // IMAGE ITEMS
  //
  // IMPORTANT:
  // Existing + new images are kept in ONE ARRAY.
  //
  // Existing:
  // {
  //   type: "existing",
  //   id,
  //   image,
  //   deleted: false
  // }
  //
  // New:
  // {
  //   type: "new",
  //   file,
  //   url
  // }
  // =========================================================

  const [imageItems, setImageItems] = useState([]);

  // =========================================================
  // DRAG IMAGE
  // =========================================================

  const [draggedImageIndex, setDraggedImageIndex] = useState(null);

  // =========================================================
  // LOADING
  // =========================================================

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // =========================================================
  // MESSAGE
  // =========================================================

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  // =========================================================
  // FILTERS
  // =========================================================

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [imageFilter, setImageFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");

  // =========================================================
  // PAGINATION
  // =========================================================

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // =========================================================
  // IMAGE MODAL
  // =========================================================

  const [imageModal, setImageModal] = useState({
    open: false,
    project: null,
    selectedIndex: 0,
  });

  // =========================================================
  // FETCH PROJECTS
  // =========================================================

  const fetchProjects = async () => {
    try {
      setFetching(true);

      const response = await fetch(`${API_URL}?action=get_projects`);

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(
          result.message || "Failed to fetch portfolio projects.",
        );
      }

      setProjects(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error("Fetch Portfolio Error:", error);

      setMessage({
        type: "error",
        text: error.message || "Failed to load portfolio projects.",
      });
    } finally {
      setFetching(false);
    }
  };

  // =========================================================
  // INITIAL FETCH
  // =========================================================

  useEffect(() => {
    fetchProjects();
  }, []);

  // =========================================================
  // INPUT CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "display_order" ? Math.max(1, Number(value) || 1) : value,
    }));
  };

  // =========================================================
  // CATEGORY
  // =========================================================

  const setCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      category,
    }));
  };

  // =========================================================
  // IMAGE SELECT
  //
  // New images are appended to imageItems.
  // They can then be dragged together with existing images.
  // =========================================================

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) {
      return;
    }

    const validFiles = files.filter((file) => file.type.startsWith("image/"));

    if (!validFiles.length) {
      setMessage({
        type: "error",
        text: "Please select valid image files.",
      });

      e.target.value = "";
      return;
    }

    const newItems = validFiles.map((file) => ({
      type: "new",
      file,
      url: URL.createObjectURL(file),
    }));

    setImageItems((prev) => [...prev, ...newItems]);

    e.target.value = "";
  };

  // =========================================================
  // REMOVE / RESTORE IMAGE
  // =========================================================

  const toggleImageDelete = (index) => {
    setImageItems((prev) =>
      prev
        .map((item, i) => {
          if (i !== index) {
            return item;
          }

          // New image -> completely remove it
          if (item.type === "new") {
            if (item.url) {
              URL.revokeObjectURL(item.url);
            }

            return null;
          }

          // Existing image -> mark/unmark deletion
          return {
            ...item,
            deleted: !item.deleted,
          };
        })
        .filter(Boolean),
    );
  };

  // =========================================================
  // DRAG START
  // =========================================================

  const handleImageDragStart = (index) => {
    if (imageItems[index]?.deleted) {
      return;
    }

    setDraggedImageIndex(index);
  };

  // =========================================================
  // DRAG OVER
  // =========================================================

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  // =========================================================
  // DROP
  //
  // Existing and new images can be reordered together.
  // =========================================================

  const handleImageDrop = (dropIndex) => {
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      return;
    }

    setImageItems((prev) => {
      const updated = [...prev];

      const draggedItem = updated[draggedImageIndex];

      if (!draggedItem || draggedItem.deleted) {
        return prev;
      }

      updated.splice(draggedImageIndex, 1);

      updated.splice(dropIndex, 0, draggedItem);

      return updated;
    });

    setDraggedImageIndex(null);
  };

  // =========================================================
  // DRAG END
  // =========================================================

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
  };

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    imageItems.forEach((item) => {
      if (item?.type === "new" && item?.url) {
        URL.revokeObjectURL(item.url);
      }
    });

    setFormData({
      title: "",
      category: "Mobile",
      app_link: "",
      display_order: projects.length + 1,
    });

    setImageItems([]);
    setDraggedImageIndex(null);
    setEditingId(null);
  };

  // =========================================================
  // EDIT PROJECT
  // =========================================================

  const handleEdit = (project) => {
    setEditingId(project.id);

    setFormData({
      title: project.title || "",
      category: project.category || "Mobile",
      app_link: project.app_link || "",
      display_order: Number(project.display_order) || 1,
    });

    // Convert existing images to common imageItems format
    const existingItems = Array.isArray(project.images)
      ? project.images.map((image) => ({
          type: "existing",
          id: Number(image.id),
          project_id: Number(image.project_id),
          image: image.image,
          display_order: Number(image.display_order),
          deleted: false,
        }))
      : [];

    setImageItems(existingItems);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // GET ACTIVE IMAGES
  // =========================================================

  const activeImageItems = imageItems.filter((item) => !item.deleted);

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage({
      type: "",
      text: "",
    });

    // =======================================================
    // VALIDATION
    // =======================================================

    if (!formData.title.trim()) {
      setMessage({
        type: "error",
        text: "Please enter project title.",
      });

      return;
    }

    if (!formData.category) {
      setMessage({
        type: "error",
        text: "Please select project category.",
      });

      return;
    }

    // =======================================================
    // IMAGE VALIDATION
    // =======================================================

    if (!editingId && activeImageItems.length === 0) {
      setMessage({
        type: "error",
        text: "Please select at least one project image.",
      });

      return;
    }

    if (editingId && activeImageItems.length === 0) {
      setMessage({
        type: "error",
        text: "Project must have at least one image.",
      });

      return;
    }

    // =======================================================
    // FORM DATA
    // =======================================================

    const uploadData = new FormData();

    uploadData.append("title", formData.title.trim());

    uploadData.append("category", formData.category);

    uploadData.append("app_link", formData.app_link.trim());

    uploadData.append("display_order", String(formData.display_order));

    // =======================================================
    // EDIT ID
    // =======================================================

    if (editingId) {
      uploadData.append("id", String(editingId));
    }

    // =======================================================
    // DELETE EXISTING IMAGES
    // =======================================================

    if (editingId) {
      imageItems.forEach((item) => {
        if (item.type === "existing" && item.deleted) {
          uploadData.append("delete_images[]", String(item.id));
        }
      });
    }

    // =======================================================
    // NEW IMAGE FILES
    //
    // IMPORTANT:
    // New files are added to FormData in exactly the
    // same order as new imageItems.
    // =======================================================

    let newImageIndex = 0;

    const imageOrder = [];

    activeImageItems.forEach((item) => {
      if (item.type === "existing") {
        imageOrder.push({
          type: "existing",
          id: Number(item.id),
        });
      }

      if (item.type === "new") {
        uploadData.append("images[]", item.file);

        imageOrder.push({
          type: "new",
          index: newImageIndex,
        });

        newImageIndex++;
      }
    });

    // =======================================================
    // FINAL IMAGE ORDER
    // =======================================================

    uploadData.append("image_order", JSON.stringify(imageOrder));

    console.log("FINAL IMAGE ORDER:", imageOrder);

    // =======================================================
    // ACTION
    // =======================================================

    const action = editingId ? "update_project" : "add_project";

    // =======================================================
    // REQUEST
    // =======================================================

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}?action=${action}`, {
        method: "POST",
        body: uploadData,
      });

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Invalid PHP response:", text);

        throw new Error(
          "Server returned an invalid response. Check PHP error log.",
        );
      }

      if (!response.ok || result.status !== "success") {
        throw new Error(result.message || "Failed to save project.");
      }

      setMessage({
        type: "success",
        text: editingId
          ? "Portfolio project updated successfully."
          : "Portfolio project added successfully.",
      });

      resetForm();

      await fetchProjects();
    } catch (error) {
      console.error("Portfolio Save Error:", error);

      setMessage({
        type: "error",
        text: error.message || "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DELETE PROJECT
  // =========================================================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this portfolio project?",
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setFetching(true);

      const data = new FormData();

      data.append("id", String(id));

      const response = await fetch(`${API_URL}?action=delete_project`, {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      if (!response.ok || result.status !== "success") {
        throw new Error(result.message || "Failed to delete project.");
      }

      setMessage({
        type: "success",
        text: "Portfolio project deleted successfully.",
      });

      if (Number(editingId) === Number(id)) {
        resetForm();
      }

      await fetchProjects();
    } catch (error) {
      console.error("Delete Portfolio Error:", error);

      setMessage({
        type: "error",
        text: error.message || "Failed to delete project.",
      });
    } finally {
      setFetching(false);
    }
  };

  // =========================================================
  // CHANGE PROJECT ORDER
  // =========================================================

  const changeOrder = (id, value) => {
    setProjects((prev) =>
      prev.map((project) =>
        Number(project.id) === Number(id)
          ? {
              ...project,
              display_order: Math.max(1, Number(value) || 1),
            }
          : project,
      ),
    );
  };

  // =========================================================
  // SAVE ALL PROJECT ORDERS
  // =========================================================

  const saveDisplayOrder = async () => {
    try {
      setLoading(true);

      const orderData = projects.map((project) => ({
        id: Number(project.id),
        display_order: Number(project.display_order) || 1,
      }));

      const data = new FormData();

      data.append("projects_json", JSON.stringify(orderData));

      const response = await fetch(`${API_URL}?action=update_order`, {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      if (!response.ok || result.status !== "success") {
        throw new Error(result.message || "Failed to update display order.");
      }

      setMessage({
        type: "success",
        text: "Display order updated successfully.",
      });

      await fetchProjects();
    } catch (error) {
      console.error("Order Update Error:", error);

      setMessage({
        type: "error",
        text: error.message || "Failed to update display order.",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // IMAGE URL
  // =========================================================

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return IMAGE_URL + image;
  };

  // =========================================================
  // FILTER + SORT
  // =========================================================

  const filteredProjects = useMemo(() => {
    let result = [...projects];

    const search = searchTerm.trim().toLowerCase();

    if (search) {
      result = result.filter((project) => {
        const title = String(project.title || "").toLowerCase();

        const category = String(project.category || "").toLowerCase();

        const link = String(project.app_link || "").toLowerCase();

        return (
          title.includes(search) ||
          category.includes(search) ||
          link.includes(search)
        );
      });
    }

    if (categoryFilter !== "All") {
      result = result.filter(
        (project) => String(project.category) === categoryFilter,
      );
    }

    if (imageFilter === "with") {
      result = result.filter(
        (project) => Array.isArray(project.images) && project.images.length > 0,
      );
    }

    if (imageFilter === "multiple") {
      result = result.filter(
        (project) => Array.isArray(project.images) && project.images.length > 1,
      );
    }

    if (imageFilter === "without") {
      result = result.filter(
        (project) => !project.images || project.images.length === 0,
      );
    }

    result.sort((a, b) => {
      const aOrder = Number(a.display_order) || 0;

      const bOrder = Number(b.display_order) || 0;

      return sortOrder === "asc" ? aOrder - bOrder : bOrder - aOrder;
    });

    return result;
  }, [projects, searchTerm, categoryFilter, imageFilter, sortOrder]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProjects.length / itemsPerPage),
  );

  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;

    return filteredProjects.slice(start, start + itemsPerPage);
  }, [filteredProjects, currentPage, itemsPerPage]);

  // =========================================================
  // RESET PAGE WHEN FILTER CHANGES
  // =========================================================

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, imageFilter, itemsPerPage, sortOrder]);

  // =========================================================
  // KEEP PAGE VALID
  // =========================================================

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // =========================================================
  // IMAGE MODAL
  // =========================================================

  const openImageModal = (project, index = 0) => {
    setImageModal({
      open: true,
      project,
      selectedIndex: index,
    });
  };

  // =========================================================
  // CLOSE IMAGE MODAL
  // =========================================================

  const closeImageModal = () => {
    setImageModal({
      open: false,
      project: null,
      selectedIndex: 0,
    });
  };

  // =========================================================
  // NEXT MODAL IMAGE
  // =========================================================

  const nextModalImage = () => {
    if (!imageModal.project?.images?.length) {
      return;
    }

    const total = imageModal.project.images.length;

    setImageModal((prev) => ({
      ...prev,
      selectedIndex: (prev.selectedIndex + 1) % total,
    }));
  };

  // =========================================================
  // PREVIOUS MODAL IMAGE
  // =========================================================

  const previousModalImage = () => {
    if (!imageModal.project?.images?.length) {
      return;
    }

    const total = imageModal.project.images.length;

    setImageModal((prev) => ({
      ...prev,
      selectedIndex: (prev.selectedIndex - 1 + total) % total,
    }));
  };

  // =========================================================
  // KEYBOARD MODAL CONTROL
  // =========================================================

  useEffect(() => {
    if (!imageModal.open) {
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeImageModal();
      }

      if (e.key === "ArrowRight") {
        nextModalImage();
      }

      if (e.key === "ArrowLeft") {
        previousModalImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [imageModal.open]);

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="portfolio-form-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="portfolio-form-header">
        <div>
          <h1>
            {editingId ? "Edit Portfolio Project" : "Add Portfolio Project"}
          </h1>

          <p>Manage your mobile apps, websites and UI/UX projects.</p>
        </div>

        <div className="portfolio-form-header-icon">
          <FaPlus />
        </div>
      </div>

      {/* =====================================================
          MESSAGE
      ===================================================== */}

      {message.text && (
        <div
          className={`portfolio-form-message ${
            message.type === "success" ? "success" : "error"
          }`}
        >
          {message.type === "success" ? <FaCheckCircle /> : <FaTimesCircle />}

          <span>{message.text}</span>

          <button
            type="button"
            onClick={() =>
              setMessage({
                type: "",
                text: "",
              })
            }
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* =====================================================
          FORM
      ===================================================== */}

      <form className="portfolio-form" onSubmit={handleSubmit}>
        <div className="portfolio-form-card">
          {/* PROJECT INFORMATION */}

          <div className="portfolio-section-title">
            <div className="portfolio-section-icon">
              <FaAlignLeft />
            </div>

            <div>
              <h2>Project Information</h2>

              <p>Enter portfolio project details.</p>
            </div>
          </div>

          {/* TITLE */}
          <div className="portfolio-category-grid">
            <div className="portfolio-form-group">
              <label>
                Project Title <span>*</span>
              </label>

              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter project title"
                disabled={loading}
              />
            </div>
            <div className="portfolio-form-group">
              <label>App / Website URL</label>

              <div className="portfolio-input-icon">
                <FaLink />

                <input
                  type="url"
                  name="app_link"
                  value={formData.app_link}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  disabled={loading}
                />
              </div>
            </div>

            {/* DISPLAY ORDER */}

            <div className="portfolio-form-group">
              <label>
                Display Order <span>*</span>
              </label>

              <div className="portfolio-input-icon">
                <FaSortNumericDown />

                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                  min="1"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          <div className="portfolio-form-group">
            <label>
              Project Category <span>*</span>
            </label>

            <div className="portfolio-category-grid">
              <button
                type="button"
                className={`portfolio-category ${
                  formData.category === "Mobile" ? "active" : ""
                }`}
                onClick={() => setCategory("Mobile")}
                disabled={loading}
              >
                <FaMobileAlt />

                <div>
                  <strong>Mobile Development</strong>

                  <small>Android / Mobile Apps</small>
                </div>
              </button>

              <button
                type="button"
                className={`portfolio-category ${
                  formData.category === "Web" ? "active" : ""
                }`}
                onClick={() => setCategory("Web")}
                disabled={loading}
              >
                <FaGlobe />

                <div>
                  <strong>Web Development</strong>

                  <small>Websites / Web Apps</small>
                </div>
              </button>

              <button
                type="button"
                className={`portfolio-category ${
                  formData.category === "UIUX" ? "active" : ""
                }`}
                onClick={() => setCategory("UIUX")}
                disabled={loading}
              >
                <FaPalette />

                <div>
                  <strong>UI / UX</strong>

                  <small>Design Concepts</small>
                </div>
              </button>
            </div>
          </div>

          {/* APP LINK */}

          {/* =================================================
              IMAGES
          ================================================= */}

          <div className="portfolio-form-group portfolio-images-section">
            <label>
              Project Images <span>*</span>
            </label>

            <div className="portfolio-images-layout">
              {/* =====================================================
        LEFT SIDE - UPLOAD BOX
    ===================================================== */}
              <div className="portfolio-images-upload-column">
                <div className="portfolio-upload-box">
                  <div className="portfolio-upload-box-icon">
                    <FaUpload />
                  </div>

                  <h3>Upload Project Images</h3>

                  <p>Select one or multiple images for this project.</p>

                  <span className="portfolio-upload-formats">
                    JPG, JPEG, PNG or WEBP
                  </span>

                  <label
                    htmlFor="portfolio-images"
                    className="portfolio-select-images-btn"
                  >
                    <FaImage />
                    Select Images
                  </label>

                  <input
                    id="portfolio-images"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChange={handleImageChange}
                    disabled={loading}
                    style={{ display: "none" }}
                  />

                  <div className="portfolio-upload-info">
                    <FaImages />
                    <span>You can select multiple images at once.</span>
                  </div>
                </div>
              </div>

              {/* =====================================================
        RIGHT SIDE - IMAGE LIST
    ===================================================== */}
              <div className="portfolio-images-list-column">
                <div className="portfolio-images-list-header">
                  <div>
                    <strong>Project Images</strong>

                    <small>Drag images to change their order</small>
                  </div>

                  <span className="portfolio-image-total">
                    {imageItems.filter((item) => !item.deleted).length}
                  </span>
                </div>

                {imageItems.length > 0 ? (
                  <div className="portfolio-preview-grid portfolio-existing-images-grid">
                    {imageItems.map((item, index) => {
                      const isDeleted = item.deleted === true;

                      return (
                        <div
                          key={
                            item.type === "existing"
                              ? `existing-${item.id}`
                              : `new-${item.file.name}-${index}`
                          }
                          className={`portfolio-preview-card portfolio-existing-image-card ${
                            isDeleted ? "marked-delete" : ""
                          } ${draggedImageIndex === index ? "dragging" : ""}`}
                          draggable={!isDeleted}
                          onDragStart={() => handleImageDragStart(index)}
                          onDragOver={handleImageDragOver}
                          onDrop={() => handleImageDrop(index)}
                          onDragEnd={handleImageDragEnd}
                        >
                          {/* ORDER */}
                          <div className="portfolio-image-order-number">
                            {index + 1}
                          </div>

                          {/* TYPE */}
                          <div className="portfolio-image-type-badge">
                            {item.type === "existing" ? "Existing" : "New"}
                          </div>

                          {/* DRAG HANDLE */}
                          {!isDeleted && (
                            <div
                              className="portfolio-image-drag-handle"
                              title="Drag to reorder"
                            >
                              <FaSortNumericDown />
                            </div>
                          )}

                          {/* IMAGE */}
                          <img
                            src={
                              item.type === "existing"
                                ? getImageUrl(item.image)
                                : item.url
                            }
                            alt={`Project ${index + 1}`}
                          />

                          {/* DELETE OVERLAY */}
                          {isDeleted && (
                            <div className="portfolio-image-delete-overlay">
                              <FaTrash />
                              <span>Will be removed</span>
                            </div>
                          )}

                          {/* DELETE / RESTORE */}
                          <button
                            type="button"
                            className={`portfolio-preview-delete ${
                              isDeleted ? "restore" : ""
                            }`}
                            onClick={() => toggleImageDelete(index)}
                            title={isDeleted ? "Restore image" : "Remove image"}
                          >
                            {isDeleted ? <FaUndo /> : <FaTrash />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="portfolio-no-images">
                    <FaImages />

                    <h3>No Images Added</h3>

                    <p>
                      Select images from the upload box to add them to this
                      project.
                    </p>
                  </div>
                )}

                {/* <small className="portfolio-help-text">
                  The first image will be used as the primary portfolio image.
                  Drag and drop to change the order.
                </small> */}
              </div>
            </div>
          </div>

          {/* =================================================
              FORM ACTIONS
          ================================================= */}

          <div className="portfolio-form-actions">
            <button
              type="button"
              className="portfolio-cancel-btn"
              onClick={resetForm}
              disabled={loading}
            >
              {editingId ? "Cancel Edit" : "Clear Form"}
            </button>

            <button
              type="submit"
              className="portfolio-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="portfolio-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  {editingId ? <FaSave /> : <FaUpload />}

                  {editingId ? "Update Portfolio" : "Add Portfolio"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* =====================================================
          PROJECTS SECTION
      ===================================================== */}

      <div className="portfolio-projects-section">
        <div className="portfolio-projects-header">
          <div>
            <h2>Portfolio Projects</h2>

            <p>
              Showing <strong>{filteredProjects.length}</strong> of{" "}
              <strong>{projects.length}</strong> projects
            </p>
          </div>

          <div className="portfolio-project-header-actions">
            <button
              type="button"
              onClick={fetchProjects}
              disabled={fetching}
              className="portfolio-refresh-btn"
            >
              <FaSyncAlt className={fetching ? "portfolio-spin" : ""} />
              Refresh
            </button>

            {/* {projects.length > 0 && (
              <button
                type="button"
                onClick={saveDisplayOrder}
                disabled={loading}
                className="portfolio-order-save-btn"
              >
                <FaSave />
                Save Display Order
              </button>
            )} */}
          </div>
        </div>

        {/* FILTER */}

        <div className="portfolio-filter-card">
          <div className="portfolio-search-box">
            <FaSearch />

            <input
              type="text"
              placeholder="Search title, category or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm("")}>
                <FaTimes />
              </button>
            )}
          </div>

          <div className="portfolio-filter-item">
            <FaFilter />

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All Categories</option>

              <option value="Mobile">Mobile</option>

              <option value="Web">Web</option>

              <option value="UIUX">UI / UX</option>
            </select>
          </div>

          <div className="portfolio-filter-item">
            <FaImages />

            <select
              value={imageFilter}
              onChange={(e) => setImageFilter(e.target.value)}
            >
              <option value="All">All Images</option>

              <option value="with">Has Images</option>

              <option value="multiple">Multiple Images</option>

              <option value="without">No Images</option>
            </select>
          </div>

          <div className="portfolio-filter-item">
            <FaSortNumericDown />

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="asc">Order: Low → High</option>

              <option value="desc">Order: High → Low</option>
            </select>
          </div>

          <div className="portfolio-filter-item">
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 / Page</option>

              <option value={10}>10 / Page</option>

              <option value={20}>20 / Page</option>

              <option value={50}>50 / Page</option>
            </select>
          </div>
        </div>

        {/* LOADING / EMPTY */}

        {fetching && projects.length === 0 ? (
          <div className="portfolio-loading">
            <FaSpinner className="portfolio-spinner" />
            Loading portfolio...
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="portfolio-empty">
            <FaImage />

            <h3>No Portfolio Projects</h3>

            <p>No project matches your current filters.</p>

            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setCategoryFilter("All");
                setImageFilter("All");
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            {/* TABLE */}

            <div className="portfolio-table-wrapper">
              <table className="portfolio-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Project</th>
                    <th>Category</th>
                    <th>Images</th>
                    <th>Order</th>
                    <th>Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedProjects.map((project, index) => {
                    const images = Array.isArray(project.images)
                      ? project.images
                      : [];

                    const serial = (currentPage - 1) * itemsPerPage + index + 1;

                    return (
                      <tr key={project.id}>
                        <td>
                          <span className="portfolio-table-number">
                            {serial}
                          </span>
                        </td>

                        <td>
                          <div className="portfolio-project-cell">
                            <div
                              className="portfolio-table-image"
                              onClick={() =>
                                images.length > 0 && openImageModal(project, 0)
                              }
                            >
                              {images[0] ? (
                                <>
                                  <img
                                    src={getImageUrl(images[0].image)}
                                    alt={project.title}
                                  />

                                  {images.length > 1 && (
                                    <span>+{images.length - 1}</span>
                                  )}
                                </>
                              ) : (
                                <FaImage />
                              )}
                            </div>

                            <div className="portfolio-project-text">
                              <strong>{project.title || "Untitled"}</strong>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`portfolio-category-badge ${String(
                              project.category || "",
                            ).toLowerCase()}`}
                          >
                            {project.category || "-"}
                          </span>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="portfolio-image-count-btn"
                            onClick={() =>
                              images.length > 0 && openImageModal(project, 0)
                            }
                            disabled={images.length === 0}
                          >
                            <FaImages />

                            {images.length}

                            <small>View</small>
                          </button>
                        </td>

                        <td>
                          <div className="portfolio-order-input">
                            <FaSortNumericDown />

                            <input
                              type="number"
                              min="1"
                              value={project.display_order}
                              onChange={(e) =>
                                changeOrder(project.id, e.target.value)
                              }
                            />
                          </div>
                        </td>

                        <td>
                          {project.app_link ? (
                            <a
                              href={project.app_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portfolio-table-link"
                              title={project.app_link}
                            >
                              <FaExternalLinkAlt />
                              Open
                            </a>
                          ) : (
                            <span className="portfolio-no-link">No Link</span>
                          )}
                        </td>

                        <td>
                          <div className="portfolio-table-actions">
                            <button
                              type="button"
                              className="portfolio-view-btn"
                              onClick={() =>
                                images.length > 0
                                  ? openImageModal(project, 0)
                                  : handleEdit(project)
                              }
                              title="View"
                            >
                              <FaEye />
                            </button>

                            <button
                              type="button"
                              className="portfolio-edit-btn"
                              onClick={() => handleEdit(project)}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>

                            <button
                              type="button"
                              className="portfolio-delete-btn"
                              onClick={() => handleDelete(project.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}

            <div className="portfolio-pagination">
              <div className="portfolio-pagination-info">
                Showing{" "}
                <strong>
                  {filteredProjects.length === 0
                    ? 0
                    : (currentPage - 1) * itemsPerPage + 1}
                </strong>
                {" - "}
                <strong>
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredProjects.length,
                  )}
                </strong>
                {" of "}
                <strong>{filteredProjects.length}</strong>
              </div>

              <div className="portfolio-pagination-buttons">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  <FaChevronLeft />
                </button>

                {Array.from(
                  {
                    length: totalPages,
                  },
                  (_, i) => i + 1,
                )
                  .filter((page) => {
                    if (totalPages <= 7) {
                      return true;
                    }

                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    );
                  })
                  .map((page, index, arr) => {
                    const previous = arr[index - 1];

                    return (
                      <React.Fragment key={page}>
                        {previous && page - previous > 1 && (
                          <span className="portfolio-pagination-dots">...</span>
                        )}

                        <button
                          type="button"
                          className={currentPage === page ? "active" : ""}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* =====================================================
          IMAGE MODAL
      ===================================================== */}

      {imageModal.open && imageModal.project && (
        <div
          className="portfolio-image-modal-overlay"
          onClick={closeImageModal}
        >
          <div
            className="portfolio-image-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="portfolio-image-modal-header">
              <div>
                <h2>{imageModal.project.title}</h2>

                <span>
                  {imageModal.selectedIndex + 1} /{" "}
                  {imageModal.project.images.length} images
                </span>
              </div>

              <button type="button" onClick={closeImageModal}>
                <FaTimes />
              </button>
            </div>

            <div className="portfolio-image-modal-main">
              {imageModal.project.images.length > 1 && (
                <button
                  type="button"
                  className="portfolio-modal-arrow left"
                  onClick={previousModalImage}
                >
                  <FaChevronLeft />
                </button>
              )}

              <img
                src={getImageUrl(
                  imageModal.project.images[imageModal.selectedIndex].image,
                )}
                alt={imageModal.project.title}
              />

              {imageModal.project.images.length > 1 && (
                <button
                  type="button"
                  className="portfolio-modal-arrow right"
                  onClick={nextModalImage}
                >
                  <FaChevronRight />
                </button>
              )}
            </div>

            {imageModal.project.images.length > 1 && (
              <div className="portfolio-modal-thumbnails">
                {imageModal.project.images.map((image, index) => (
                  <button
                    type="button"
                    key={image.id || index}
                    className={
                      imageModal.selectedIndex === index ? "active" : ""
                    }
                    onClick={() =>
                      setImageModal((prev) => ({
                        ...prev,
                        selectedIndex: index,
                      }))
                    }
                  >
                    <img src={getImageUrl(image.image)} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
